import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { AttachmentCuInputObject } from 'graphql-common';
import { Values } from '@lib/interfaces/form';

// Shared rules for turning a form value into an `*Attached` GraphQL input field.
//
// A form value for an attached file can be in one of these shapes:
//   undefined / missing         → field omitted
//   null                        → { _destroy: true }
//   { urls, id, ... }           → from API, unchanged → field omitted
//   { url, file, filename }     → new upload with local preview → { file, filename }
//   { fileTag }                 → preset tag (asset icon only) → { fileTag }
//   { id, _destroy: true }      → explicit destroy marker (array items)

export interface PrepareAttachedFieldOptions {
  // Emit `file: { id, storage, metadata }` instead of passing the whole file
  // object through. Matches the asset / asset-documentation payload shape.
  pickFileFields?: boolean;
  // Whether to include `filename` on a new-upload payload. Default true.
  includeFilename?: boolean;
  // Whether `fileTag` form values should produce `{ fileTag }` output.
  // Only true for the asset icon today.
  allowFileTag?: boolean;
}

export function prepareAttachedField(
  value: unknown,
  options: PrepareAttachedFieldOptions = {},
): AttachmentCuInputObject | undefined {
  const {
    pickFileFields = false,
    includeFilename = true,
    allowFileTag = false,
  } = options;

  if (value === undefined) return undefined;
  if (value === null) return { _destroy: true };
  if (typeof value !== 'object') return undefined;

  const obj = value as Record<string, unknown>;

  // Existing API attachment, unchanged — don't send.
  if (obj.urls) return undefined;

  // New upload. File takes precedence over fileTag, so picking a real file
  // overrides a previously-selected preset tag.
  const file = obj.file as Record<string, unknown> | undefined;
  if (file && file.id) {
    const result: Record<string, unknown> = {
      file: pickFileFields ? _pick(file, ['id', 'storage', 'metadata']) : file,
    };
    if (includeFilename) {
      result.filename = obj.filename;
    }
    return result as AttachmentCuInputObject;
  }

  // Preset tag (asset icon).
  if (allowFileTag && obj.fileTag) {
    return { fileTag: obj.fileTag as string };
  }

  return undefined;
}

// Array counterpart for `attachmentsAttached` fields.
//   - null item                    → { _destroy: true }
//   - explicit _destroy on an item → { id, _destroy: true } (handled first so
//                                     existing API rows marked for removal
//                                     aren't silently dropped by the urls
//                                     filter below)
//   - item with `url` (preview)     → strip `url`, pass through
//   - item without `url`            → strip `file` (so unchanged API rows
//                                     surface their `urls` field)
//   - items with `urls`             → dropped (unchanged API rows)
//   - if the whole array is missing but `prevValues[fieldName]` existed, mark
//     all previous items for destruction
export function prepareAttachedFieldList(
  values: Values,
  prevValues: Values,
  fieldName: string,
): AttachmentCuInputObject[] {
  const attachmentsAttached: AttachmentCuInputObject[] = [];
  if (!values || !prevValues || !fieldName) return attachmentsAttached;

  const attachments = _get(values, fieldName) as unknown;
  const prevAttachments = _get(prevValues, fieldName) as unknown;

  if (Array.isArray(attachments)) {
    attachments.forEach((attachment) => {
      if (attachment === null) {
        attachmentsAttached.push({ _destroy: true });
        return;
      }
      if (!attachment || typeof attachment !== 'object') return;

      const item = attachment as Record<string, unknown>;

      // Explicit destroy marker wins — one entry, regardless of other fields.
      if (_get(item, '_destroy')) {
        attachmentsAttached.push({
          id: item.id as string,
          _destroy: true,
        });
        return;
      }

      const attachmentValue = !_isEmpty(item.url)
        ? _omit(item, 'url')
        : _omit(item, 'file');

      // Unchanged API rows surface `urls` and are dropped.
      if (!attachmentValue.urls) {
        attachmentsAttached.push(attachmentValue as AttachmentCuInputObject);
      }
    });
  } else if (Array.isArray(prevAttachments)) {
    prevAttachments.forEach((attachment) => {
      const id = _get(attachment, 'id') as string;
      if (id) attachmentsAttached.push({ id, _destroy: true });
    });
  }

  return attachmentsAttached;
}

export default prepareAttachedField;
